import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Eye, EyeOff } from "lucide-react";
import {
  sendForgotPasswordOTP,
  verifyForgotPasswordOTP,
  verifyForgotPasswordCode,
} from "../services/api";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();

  // Force light theme on this page
  useEffect(() => {
    // Store original theme state
    const hadDarkClass = document.documentElement.classList.contains("dark");
    const savedTheme = localStorage.getItem("theme") || "LIGHT";

    // Force light theme
    document.documentElement.classList.remove("dark");

    // Restore original theme on unmount
    return () => {
      // Check if we should restore dark theme based on saved preference
      if (
        savedTheme === "DARK" ||
        (savedTheme === "SYSTEM" &&
          window.matchMedia("(prefers-color-scheme: dark)").matches)
      ) {
        document.documentElement.classList.add("dark");
      } else if (hadDarkClass && savedTheme !== "LIGHT") {
        // If it was dark before and not explicitly LIGHT, restore it
        document.documentElement.classList.add("dark");
      }
    };
  }, []);
  const [step, setStep] = useState(1); // 1: email, 2: OTP, 3: new password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      const response = await sendForgotPasswordOTP(email.trim());
      setMaskedEmail(response.data.email);
      setStep(2);
      toast.success("OTP sent successfully! Check your email.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp.trim() || otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      await verifyForgotPasswordCode(email.trim(), otp.trim());
      setStep(3);
      toast.success("OTP verified successfully! Now set your new password.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to verify OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword.trim()) {
      toast.error("Please enter a new password");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);
    try {
      await verifyForgotPasswordOTP(email.trim(), otp.trim(), newPassword);
      toast.success(
        "Password reset successfully! You can now login with your new password.",
      );
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      await sendForgotPasswordOTP(email.trim());
      toast.success("New OTP sent successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="max-w-md mx-auto">
      <h2 className="text-2xl font-bold text-text-primary mb-6 text-center">
        Forgot Password
      </h2>
      <p className="text-text-secondary mb-6 text-center">
        Enter your email address and we'll send you a one-time password to reset
        your password.
      </p>

      <form onSubmit={handleSendOTP} className="space-y-4">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-text-primary mb-1"
          >
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 bg-bg-primary text-text-primary border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your email"
            required
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Sending..." : "Send OTP"}
        </button>
      </form>

      <div className="mt-6 text-center">
        <Link to="/login" className="text-blue-600 hover:text-blue-500 text-sm">
          Back to Login
        </Link>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="max-w-md mx-auto">
      <h2 className="text-2xl font-bold text-text-primary mb-6 text-center">
        Enter OTP
      </h2>
      <p className="text-text-secondary mb-6 text-center">
        We've sent a 6-digit OTP to{" "}
        <span className="font-medium">{maskedEmail}</span>
      </p>

      <form onSubmit={handleVerifyOTP} className="space-y-4">
        <div>
          <label
            htmlFor="otp"
            className="block text-sm font-medium text-text-primary mb-1"
          >
            One-Time Password
          </label>
          <input
            type="text"
            id="otp"
            value={otp}
            onChange={(e) =>
              setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            className="w-full px-3 py-2 bg-bg-primary text-text-primary border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg tracking-widest"
            placeholder="000000"
            maxLength={6}
            required
          />
        </div>

        <button
          type="submit"
          disabled={loading || otp.length !== 6}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Verifying..." : "Verify OTP"}
        </button>
      </form>

      <div className="mt-6 text-center space-y-2">
        <button
          onClick={handleResendOTP}
          disabled={loading}
          className="text-blue-600 hover:text-blue-500 text-sm disabled:opacity-50"
        >
          Resend OTP
        </button>
        <div>
          <button
            onClick={() => setStep(1)}
            className="text-text-secondary hover:text-text-tertiary text-sm"
          >
            Change Email
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="max-w-md mx-auto">
      <h2 className="text-2xl font-bold text-text-primary mb-6 text-center">
        Set New Password
      </h2>
      <p className="text-text-secondary mb-6 text-center">
        Enter your new password below
      </p>

      <form
        onSubmit={handleResetPassword}
        className="space-y-4"
        autoComplete="off"
      >
        <div>
          <label
            htmlFor="newPassword"
            className="block text-sm font-medium text-text-primary mb-1"
          >
            New Password
          </label>
          <div className="relative">
            <input
              type={showNewPassword ? "text" : "password"}
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full px-3 py-2 pr-10 bg-bg-primary text-text-primary border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter new password"
              required
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-text-tertiary hover:text-text-secondary focus:outline-none"
              tabIndex={-1}
            >
              {showNewPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
          <p className="text-xs text-text-tertiary mt-1">
            Must be at least 8 characters with uppercase, lowercase, and number
          </p>
        </div>

        <div>
          <label
            htmlFor="confirmPassword"
            className="block text-sm font-medium text-text-primary mb-1"
          >
            Confirm New Password
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full px-3 py-2 pr-10 bg-bg-primary text-text-primary border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Confirm new password"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-text-tertiary hover:text-text-secondary focus:outline-none"
              tabIndex={-1}
            >
              {showConfirmPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={
            loading || newPassword !== confirmPassword || newPassword.length < 8
          }
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Resetting..." : "Reset Password"}
        </button>
      </form>

      <div className="mt-6 text-center">
        <button
          onClick={() => setStep(2)}
          className="text-text-secondary hover:text-text-tertiary text-sm"
        >
          Back to OTP
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-bg-secondary flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-bg-primary py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
